import React, { useCallback, useRef } from 'react';
import { Button, Icon } from '@servicetitan/anvil2';
import VolumeUpIcon from '@servicetitan/anvil2/assets/icons/material/round/volume_up.svg';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import type { AudioPlaybackTestService } from '../../utils/audioPlaybackTestService';
import { audioDeviceStrings } from '../audioDeviceStrings';
import styles from './AudioDevicePanel.module.css';

export interface AudioTestButtonProps {
  deviceId: string | undefined;
  isTesting: boolean;
  disabled: boolean;
  testService: AudioPlaybackTestService;
  setTesting: (value: boolean) => void;
  setError: (value: string | null) => void;
  'data-testid': string;
}

export const AudioTestButton: React.FC<AudioTestButtonProps> = ({
  deviceId,
  isTesting,
  disabled,
  testService,
  setTesting,
  setError,
  'data-testid': dataTestId,
}) => {
  const cleanupRef = useRef({ testService, setTesting });
  cleanupRef.current = { testService, setTesting };

  // Stop any in-flight playback and reset state when the button unmounts
  // (e.g. user disables ring output while a test is playing).
  // Uses cleanupRef so the effect can stay dep-free ([]) and only fire on
  // true unmount, not on every render when inline callbacks get new identities.
  React.useEffect(() => {
    return () => {
      cleanupRef.current.testService.stopTest();
      cleanupRef.current.setTesting(false);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (isTesting || !deviceId) {
      return;
    }

    setTesting(true);
    setError(null);

    try {
      await testService.playSpeakerTest(deviceId);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      DiagnosticsModule.reportBackgroundError(
        error,
        audioDeviceStrings.errors.speakerTestFailed,
      );
      setError(audioDeviceStrings.errors.speakerTestFailed);
    } finally {
      setTesting(false);
    }
  }, [isTesting, deviceId, testService, setTesting, setError]);

  return (
    <Button
      appearance='secondary'
      disabled={disabled}
      onClick={handleClick}
      data-testid={dataTestId}
      className={styles.testButton}
    >
      <Icon svg={VolumeUpIcon} size='medium' />
      {isTesting
        ? audioDeviceStrings.testButtons.playingSpeaker
        : audioDeviceStrings.testButtons.testSpeaker}
    </Button>
  );
};
